import { createTheme, ThemeOptions } from '@mui/material';
import { green, grey, orange, red } from '@mui/material/colors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LinkBehavior } from './LinkBehavior';

export const branding = {
  fontFamily: 'Lato, sans-serif',
  paletteOptions: {
    primary: {
      contrastText: '#ffffff',
      25: 'E8F3D9',
      50: '#D8EBBF',
      100: '#C9E4A6',
      200: '#C9DC8C',
      300: '#A9D472',
      400: '#9ACD59',
      500: '#8AC53F',
      600: '#74A535',
      700: '#577B27',
      800: '#486620',
      900: '#324716',
      950: '#1C270C',
    },
    grey: {
      25: '#FBFBFB',
      50: '#F3F3F3',
      100: '#E1E1E2',
      200: '#D4D5D5',
      300: '#AEAEAF',
      400: '#878889',
      500: '#6D6E70',
      600: '#5E5F60',
      700: '#4F4F50',
      800: '#3F4041',
      900: '#303031',
      950: '#212121',
    },
  },
};

/**
 * If adding new mixins or variables to this file remember to update theme.d.ts definitions too
 */

// theme is split into two parts to allow second part to reference variables declared in first
const baseTheme = createTheme({
  typography: {
    // text size reset
    fontSize: 24,
    fontFamily: branding.fontFamily,
    button: {
      textTransform: 'none',
    },
    h1: {
      fontSize: '4.8rem',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          position: 'initial',
          boxShadow: 'none',
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
        LinkComponent: LinkBehavior,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontSize: '1.8rem',
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      },
      styleOverrides: {
        root: {
          textDecoration: 'none',
          ':hover': {
            color: 'inherit',
            textDecoration: 'none',
          },
        },
      },
    },
  },
  palette: {
    primary: {
      main: branding.paletteOptions.primary[700],
      light: branding.paletteOptions.primary[600],
      dark: branding.paletteOptions.primary[800],
      ...branding.paletteOptions.primary,
    },
    secondary: {
      main: branding.paletteOptions.grey[500],
      light: branding.paletteOptions.grey[200],
      dark: branding.paletteOptions.grey[700],
    },
    text: {
      primary: '#000000',
    },
    background: {
      default: grey[100],
    },
    success: {
      main: green[500],
      light: green[50],
      dark: green[800],
    },
    warning: {
      main: '#EF6C00',
      light: orange[50],
      dark: orange[700],
    },
    error: {
      main: red[500],
      light: red[50],
      dark: red[700],
    },
    info: {
      main: '#00A9E0',
      light: '#E5F7FC',
      dark: '#014361',
    },
    shadows: {
      light: '#10182808',
      dark: '#10182814',
    },
    grey: branding.paletteOptions.grey,
  },
});

const borderRadius = baseTheme.spacing(1);

// TODO: Hoist the Paper style here to all MuiPaper
const paperStyles = {
  borderRadius,
  boxShadow: `0px 2px 4px -2px ${baseTheme.palette.shadows.light}, 0px 4px 8px -2px ${baseTheme.palette.shadows.dark}`,
  border: `1px solid ${baseTheme.palette.grey[100]}`,
  marginTop: baseTheme.spacing(0.5),
};

const getAlertStyles = (severity: 'info' | 'error' | 'success' | 'warning') => ({
  backgroundColor: baseTheme.palette[severity].light,
  borderColor: baseTheme.palette[severity].main,
  color: baseTheme.palette[severity].dark,
});

const getButtonStyles = (color: 'primary' | 'secondary', variant: 'text' | 'outlined' | 'contained') => {
  switch (variant) {
    case 'outlined':
      if (color === 'primary') {
        return {
          borderWidth: '2px',
          backgroundColor: '#ffffff',
          borderColor: baseTheme.palette.primary[400],
          color: baseTheme.palette.primary[700],

          '&:hover': {
            backgroundColor: baseTheme.palette.primary[25],
            borderColor: baseTheme.palette.primary[100],
          },
        };
      } else {
        return {
          backgroundColor: '#ffffff',
          borderColor: baseTheme.palette.grey[200],
          color: baseTheme.palette.grey[500],

          '&:hover': {
            backgroundColor: baseTheme.palette.grey[50],
            borderColor: baseTheme.palette.grey[400],
          },
        };
      }

    case 'contained':
      if (color === 'primary') {
        return {
          border: `1px solid ${baseTheme.palette.primary[700]}`,
          color: 'white',
          backgroundColor: baseTheme.palette.primary[700],

          '&:hover': {
            border: `1px solid ${baseTheme.palette.primary[300]}`,
            backgroundColor: baseTheme.palette.primary[900],
          },
        };
      } else {
        return {};
      }
    default:
      return {};
  }
};

export const theme = createTheme(baseTheme, {
  mixins: {
    // App toolbar height should be controlled from here so useAppBarHeight can pick it up
    toolbar: {
      minHeight: 80,
      '@media (min-width:600px)': { minHeight: 80 },
      '@media (min-width:0px) and (orientation: landscape)': { minHeight: 80 },
    },
    modalStyle: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      maxHeight: '75vh',
      display: 'flex',
      transform: 'translate(-50%, -50%)',
      width: 400,
      bgcolor: 'background.paper',
      borderRadius,
      border: 'none',
      outline: 'none',
      p: 3,
    },
    activeButton: {
      border: `2px solid ${baseTheme.palette.primary[500]} !important`,
      boxShadow: `0px 0px 0px 4px ${baseTheme.palette.primary[50]}`,
      '&:hover': { backgroundColor: 'white !important' },
    },
    roundedIconButton: {
      borderWidth: '1px',
      borderRadius: baseTheme.spacing(1),
      borderStyle: 'solid',
    },
  },
  components: {
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          height: 4,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        rounded: {
          borderRadius,
        },
      },
    },
    MuiPaper: { styleOverrides: { root: paperStyles } },
    MuiFormControl: {
      styleOverrides: {
        root: {
          gap: 2,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          color: baseTheme.palette.grey[700],
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: ExpandMoreIcon,
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <ExpandMoreIcon />,
      },
    },
    MuiPickersCalendarHeader: {
      defaultProps: {
        slots: {
          switchViewIcon: ExpandMoreIcon,
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          '& .MuiMenuItem-root + .MuiDivider-root': {
            marginTop: 0,
            marginBottom: 0,
          },
        },
      },
    },
    MuiDivider: {
      defaultProps: {
        variant: 'middle',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&, input, fieldset': {
            borderRadius,
            borderColor: baseTheme.palette.grey[300],
          },
          '&:not(:is(:focus, .Mui-disabled)):hover fieldset.MuiOutlinedInput-notchedOutline': {
            borderColor: baseTheme.palette.primary.light,
            borderWidth: '2px',
            textDecoration: 'none',
          },
          '&:is(.Mui-focused, :focus) fieldset.MuiOutlinedInput-notchedOutline': {
            borderColor: `${baseTheme.palette.primary.main} !important`,
            boxShadow: `0px 0px 0px 4px ${baseTheme.palette.primary.light}7A`,
          },
          '&:is(.Mui-disabled)': {
            backgroundColor: `${baseTheme.palette.grey[50]}`,
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          boxShadow: `0px 1px 3px 0px ${baseTheme.palette.shadows.light}, 0px 1px 2px 0px ${baseTheme.palette.shadows.dark}`,
          borderRadius,
          borderCollapse: 'separate',
          border: `1px solid ${baseTheme.palette.grey[50]}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: baseTheme.palette.grey[50],
          fontWeight: 500,
          '& th:first-of-type': {
            borderTopLeftRadius: borderRadius,
          },
          '& th:last-of-type': {
            borderTopRightRadius: borderRadius,
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& tr:last-of-type': {
            // Remove bottom border from last row
            '& td': {
              borderBottom: 'none',
            },
            '& td:first-of-type': {
              borderBottomLeftRadius: borderRadius,
            },
            'td:last-of-type': {
              borderBottomRightRadius: borderRadius,
            },
          },
        },
      },
    },
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: {
          '& .MuiButtonBase-root': {
            borderRadius,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderRadius,
          '&.MuiAlert-outlinedInfo': getAlertStyles('info'),
          '&.MuiAlert-outlinedError': getAlertStyles('error'),
          '&.MuiAlert-outlinedSuccess': getAlertStyles('success'),
          '&.MuiAlert-outlinedWarning': getAlertStyles('warning'),
          // Used to style alert content when it's an HTML string
          '& .MuiAlert-message *': {
            color: 'inherit',
            margin: 0,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: `0px 1px 2px 0px ${baseTheme.palette.grey[700]}, 0px 1px 3px 0px ${baseTheme.palette.grey[300]}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          padding: `${baseTheme.spacing(1)} ${baseTheme.spacing(1.5)}`,
          borderRadius,
          fontSize: '14px',
          // Styles for outlined buttons
          '&.MuiButton-outlined:not(.Mui-disabled)': {
            '&.MuiButton-colorSecondary': getButtonStyles('secondary', 'outlined'),
            '&.MuiButton-colorPrimary': getButtonStyles('primary', 'outlined'),
          },
          // Styles for contained buttons
          '&.MuiButton-contained:not(.Mui-disabled)': {
            boxShadow: 'none',
            '&.MuiButton-colorPrimary': getButtonStyles('primary', 'contained'),
          },
          // Used for buttons that render as a link
          '&.MuiButton-containedPrimary[href]:hover': {
            color: baseTheme.palette.primary.contrastText,
          },
          '&.Mui-disabled': {
            backgroundColor: baseTheme.palette.grey[50],
            color: baseTheme.palette.grey[400],
            border: `1px solid ${baseTheme.palette.grey[200]}`,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:not(.Mui-disabled)': {
            '&.MuiIconButton-colorSecondary': getButtonStyles('secondary', 'outlined'),
            '&.MuiIconButton-colorPrimary': getButtonStyles('primary', 'outlined'),
          },
          '&.Mui-disabled': {
            backgroundColor: baseTheme.palette.grey[50],
            color: baseTheme.palette.grey[400],
            border: `1px solid ${baseTheme.palette.grey[200]}`,
          },
          '&.openDatePickerIconButton.Mui-disabled': {
            border: 'none',
          },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        rounded: {
          borderRadius,
        },
      },
    },
  },
} as ThemeOptions);
